import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './components/BrandingProvider';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OfflineProvider } from './contexts/OfflineContext';
import Routes from './Routes';
import ErrorBoundary from './components/ErrorBoundary';
import { useStartupHealthCheck } from './hooks/useStartupHealthCheck';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import { startErrorWatcher } from './utils/errorWatcher';

// Create a React Query client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (TanStack Query v5)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => {
  // Run startup health check (non-blocking, executes once per session)
  useStartupHealthCheck();

  // Initialize error watcher for runtime monitoring
  useEffect(() => {
    startErrorWatcher();
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <OfflineProvider>
            <AuthProvider>
              <BrandingProvider>
                <ThemeProvider>
                  <ToastProvider>
                    <PWAInstallPrompt />
                    <OfflineIndicator />
                    <Routes />
                    <Toaster />
                  </ToastProvider>
                </ThemeProvider>
              </BrandingProvider>
            </AuthProvider>
          </OfflineProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
};

export default App;
